import Sidebar from "../components/sidebar";
import AddJob from "../components/addjob";
import { useState, useEffect } from 'react';
import { fetchJobs, deleteJob, editJob } from '../services/jobHandler';
import { marked } from 'marked';

interface Job {
  id: string;
  title: string;
  location: string;
  type: string;
  description: string[];
}

const JobManagement = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [showAddJobs, setShowAddJobs] = useState(false);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [editedJob, setEditedJob] = useState<Partial<Job>>({});

  useEffect(() => {
    if (!showAddJobs) {
      const reloadJobs = async () => {
        try {
          const fetchedJobs = await fetchJobs();
          setJobs(fetchedJobs);
        } catch (error) {
          console.error("Error loading jobs:", error);
        }
      };
      reloadJobs();
    }
  }, [showAddJobs]);  
  

  const handleDelete = async (id: string) => {
    try {
      await deleteJob(id);
      setJobs(jobs.filter(job => job.id !== id));
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  const handleSaveEdit = async () => {
    try {
      if (isEditing) {
        const updatedJob = {
          id: editedJob.id,
          title: editedJob.title,
          location: editedJob.location,
          type: editedJob.type,
          description: editedJob.description || []
        };
  
        await editJob(
          updatedJob.id, // Ensure 'id' is passed first
          updatedJob.title,
          updatedJob.location,
          updatedJob.type,
          updatedJob.description
        );
  
        // Update the jobs array in state
        setJobs(jobs.map(job => (job.id === isEditing ? { ...job, ...updatedJob } : job)));
        setIsEditing(null); // Reset editing state
      }
    } catch (error) {
      console.error('Error updating job:', error);
    }
  };

  const handleEdit = (job: Job) => {
    setIsEditing(job.id);
    setEditedJob(job);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedJob(prev => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const updatedDescriptions = [...(editedJob.description || [])];
    updatedDescriptions[index] = value;
    setEditedJob(prev => ({ ...prev, description: updatedDescriptions }));
  };

  const handleAddDescription = () => {
    setEditedJob(prev => ({
      ...prev,
      description: [...(prev.description || []), '']
    }));
  };

  const handleRemoveDescription = (index: number) => {
    const updatedDescriptions = [...(editedJob.description || [])];
    updatedDescriptions.splice(index, 1);
    setEditedJob(prev => ({ ...prev, description: updatedDescriptions }));
  };

  return (
    <div className="max-w-full overflow-x-hidden">
      <Sidebar />
      {showAddJobs && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="max-w-4xl w-3/4">
            <AddJob onClose={() => setShowAddJobs(false)} />
          </div>
        </div>
      )}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">Recruitment</h1>
            <p className="mt-2 text-sm text-gray-700">
              A list of all the jobs you post.
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={() => setShowAddJobs(true)}
            >
              Post a new job
            </button>
          </div>
        </div>
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-hidden sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="w-full max-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Title
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Location
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Type
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    >
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {jobs.map((job) => (
                    <tr key={job.id}>
                      {isEditing === job.id ? (
                        <>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <input
                              type="text"
                              name="title"
                              value={editedJob.title || ''}
                              onChange={handleInputChange}
                              className="border rounded p-1 w-full"
                            />
                          </td>
                          <td className="whitespace-normal break-words px-3 py-4 text-sm text-gray-500">
                            <textarea
                              name="location"
                              value={editedJob.location || ''}
                              onChange={handleInputChange}
                              className="border rounded p-1 w-full"
                              rows={2}
                              style={{ resize: 'both' }}
                            />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <input
                              type="text"
                              name="type"
                              value={editedJob.type || ''}
                              onChange={handleInputChange}
                              className="border rounded p-1 w-full"
                            />
                          </td>
                          <td className="whitespace-normal break-words px-3 py-4 text-sm text-gray-500">
                            {editedJob.description?.map((desc, index) => (
                              <div key={index} className="mb-2 flex items-center">
                                <textarea
                                  value={desc}
                                  onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                  className="border rounded p-1 w-full"
                                  rows={3}
                                  style={{ resize: 'both' }}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveDescription(index)}
                                  className="ml-2 text-red-600"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={handleAddDescription}
                              className="text-blue-600"
                            >
                              Add Description
                            </button>
                          </td>
                          <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex justify-center space-x-4 mt-4 px-4">
                              <button
                                onClick={handleSaveEdit}
                                className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setIsEditing(null)}
                                className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{job.title}</td>
                          <td className="whitespace-normal break-words px-3 py-4 text-sm text-gray-500">{job.location}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{job.type}</td>
                          <td className="whitespace-normal break-words px-3 py-4 text-sm text-gray-500">
                            <ul>
                              {job.description.map((desc, index) => (
                                <li key={index}>
                                  {<div dangerouslySetInnerHTML={{ __html: marked(desc) as string }} />}
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <button
                              onClick={() => handleEdit(job)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(job.id)}
                              className="text-red-600 hover:text-red-900 ml-4"
                            >
                              Delete
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>    
  );
};

export default JobManagement;